import { useSelector } from "react-redux";
import { selectAuthentication } from "../../services/authorization/slices";
import { compareAuthentication } from "../../services/util/selectorEquality";
import { useNavigate, matchRoutes, useLocation } from 'react-router-dom';
import PATHS, {authenticationFlowPaths} from "../../Routes/paths";
import { AUTH_ROLES } from "../../util/constants";

/**
 * Custom React hook for role-based authentication and authorization.
 * 
 * @param {Object} [options] - Configuration options for the hook.
 * @param {string[]} [options.registeredQueries] - List of registered GraphQL query keys to verify.
 * @param {boolean} [options.redirectOnUnauthorized] - Whether to redirect to an unauthorized page if the user lacks access.
 * @param {boolean} [options.autoMode] - If true, automatically verifies the most recent GraphQL query.
 * 
 * @returns {Object} - An object containing authentication state and role-based access flags.
 */
function useRoleAuthentication(options = { registeredQueries: [], redirectOnUnauthorized: false, autoMode: false }) {
    const { redirectOnUnauthorized, autoMode } = options;
    let { registeredQueries } = options;
    const nav = useNavigate();

    if (typeof registeredQueries === "string") {
        registeredQueries = [registeredQueries];
    }
    const authenticationState = useSelector(selectAuthentication, compareAuthentication);

    const useVerifyRegisteredServiceCalls = () => {
        const queryState = useSelector(state => {
            const queries = state.graphqlApi.queries;
            if (Object.keys(queries).length === 0) {
                return true;
            }
            // In auto mode, check all queries
            if (autoMode) {
                const queriesArray = Object.values(queries).sort((a, b) => {
                    const timeA = a.fulfilledTimeStamp || a.startedTimeStamp || 0;
                    const timeB = b.fulfilledTimeStamp || b.startedTimeStamp || 0;
                    return timeB - timeA; 
                });
                
                return queriesArray.length > 0 ? queriesArray[0] : true;
            }
            
            // Filter by registered queries if provided
            if (registeredQueries && registeredQueries.length > 0) {
                const filteredQueries = Object.entries(queries)
                .filter(([key]) => registeredQueries.some(q => key.startsWith(q)))
                .map(([_, value]) => value)
                .sort((a, b) => {
                    const timeA = a.fulfilledTimeStamp || a.startedTimeStamp || 0;
                    const timeB = b.fulfilledTimeStamp || b.startedTimeStamp || 0;
                    return timeB - timeA; 
                });
                
                console.log("filteredQueries", filteredQueries)
                return filteredQueries.length > 0 ? filteredQueries[0] : true;
            }
            
            return true;
        });
    
        if (queryState?.error?.code === "Unauthorized") {
            return false;
        } else {
            return true;
        }
    }

    const useVerifyPaths = (userRoles) => {
        // Get current path from react router
        const location = useLocation()
        const combinedPaths = [...PATHS, ...authenticationFlowPaths];
        const [{ route }] = matchRoutes(combinedPaths, location)
        const currentPath = combinedPaths.find(path => path.path === route.path);
        const pathRoles = currentPath?.roles?.split(",").map(role => role.trim()) ?? [];
        // compare with user roles
        if (userRoles) {
            const hasAccess = pathRoles.some(role => userRoles.includes(role));
            return hasAccess;
        }
        else if(!pathRoles) {
            return true;
        }

        return false
    }
    
    const isApiAuthorized = useVerifyRegisteredServiceCalls();
    
    
    
    // General purpose
    const isLoggedIn = authenticationState.isLoggedIn;
    const user = authenticationState.user ?? {};
    const roles = user.applicationRoles?.filter((r) => r.length > 0) ?? [];
    
    const pathRoles = useVerifyPaths(roles);
    const verifyAnyAdmin = () => {
        // Check if the user has any admin role
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.ADMIN_ACCESS));
    }
    
    const verifyAnyReviewer = () => {
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.READ_ACCESS));
    }
    
    
    const verifyHonorsAdmin = () => {
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.HONORS_ADMIN));
    }
    const verifyHonorsReviewer = () => {
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.HONORS_REVIEWER));
    }
    const verifyScholarsAdmin = () => {
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.SCHOLARS_ADMIN));
    }
    const verifyScholarsReviewer = () => {
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.SCHOLARS_REVIEWER));
    }
    const verifyStudent = () => {
        return roles.includes(AUTH_ROLES.STUDENT_EMPLOYEE);
    }
    const verifyHonorsSeasonalManager = () => {
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.HONORS_SEASONAL_MANAGER));
    }
    const verifyHonorsProgramCoordinator = () => {
        return (isApiAuthorized && pathRoles && roles.includes(AUTH_ROLES.HONORS_PROGRAM_COORDINATOR));
    }


    // Role-specific stuff used around app
    const isHonorsAdmin = verifyHonorsAdmin();
    const isHonorsReviewer = verifyHonorsReviewer();
    const isScholarsAdmin = verifyScholarsAdmin();
    const isScholarsReviewer = verifyScholarsReviewer();
    const isAnyAdmin = verifyAnyAdmin();
    const isAnyReviewer = verifyAnyReviewer();
    const isStudent = verifyStudent();
    const isHonorsSeasonalManager = verifyHonorsSeasonalManager();
    const isHonorsProgramCoordinator = verifyHonorsProgramCoordinator();

    if (!isHonorsAdmin && !isHonorsReviewer && !isScholarsAdmin && !isScholarsReviewer && !isAnyAdmin && !isStudent && !isHonorsSeasonalManager && !isHonorsProgramCoordinator) {
        if (redirectOnUnauthorized === true) {
            nav("/unauthorized");
        }
    }

    return {
        ...authenticationState,
        isLoggedIn,
        user,
        roles,

        isHonorsAdmin,
        isHonorsReviewer,
        isScholarsAdmin,
        isScholarsReviewer,
        isAnyAdmin,
        isAnyReviewer,
        isStudent,
        isHonorsSeasonalManager,
        isHonorsProgramCoordinator,
    };
}

export default useRoleAuthentication;